// Fee & Obligation PR-6 — "Explain this obligation" AI modal. Clone of the
// Waterfall Coach's WaterfallExplainModal: auto-fires the first explanation on
// open, keeps a running staff/assistant transcript, trims history to the
// server's 12-turn cap, and lets the user ask follow-ups.

import { useEffect, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useExplainObligation } from "@/hooks/portfolioAdmin/useObligationLedger";
import type { ObligationExplainTurn } from "@/api/portfolioAdmin/obligationLedgerApi";

export interface ObligationExplainModalProps {
  loanId: string;
  obligationId: string;
  onClose: () => void;
  /** Human label of the obligation, for the modal subtitle. */
  obligationLabel?: string;
}

export function ObligationExplainModal({
  loanId,
  obligationId,
  onClose,
  obligationLabel,
}: ObligationExplainModalProps) {
  const explain = useExplainObligation(loanId, obligationId);
  const [turns, setTurns] = useState<ObligationExplainTurn[]>([]);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const ask = (q: string | null) => {
    // History = the transcript BEFORE this question; trimmed to the server's
    // 12-turn cap so a long conversation degrades instead of 400ing.
    const trimmed = turns.slice(-12);
    const history = trimmed.length > 0 ? trimmed : null;
    if (q) {
      setTurns((cur) => [...cur, { role: "user", content: q }]);
      setQuestion("");
    }
    setErrorMessage(null);
    setPending(true);
    explain.mutate(
      { question: q, history },
      {
        onSuccess: (res) =>
          setTurns((cur) => [...cur, { role: "assistant", content: res.answer }]),
        onError: (err) =>
          setErrorMessage(
            err instanceof Error && err.message
              ? err.message
              : "The explainer couldn't reply. Try again.",
          ),
        onSettled: () => setPending(false),
      },
    );
  };

  useEffect(() => {
    ask(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = question.trim();

  return (
    <Modal
      open
      onClose={onClose}
      title="Explain this obligation"
      description={obligationLabel}
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="obligation-explain-modal">
        <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
          {turns.map((t, i) => (
            <li key={i} data-testid={`explain-turn-${t.role}`}>
              <p className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
                {t.role === "user" ? "You asked" : "Explainer"}
              </p>
              <div
                className={
                  "rounded-md border border-border-light p-space-3 text-body whitespace-pre-wrap " +
                  (t.role === "user" ? "bg-white-warm" : "bg-white-pure")
                }
              >
                {t.content}
              </div>
            </li>
          ))}
        </ol>

        {pending && (
          <p className="text-body text-gray-mid italic m-0" data-testid="explain-thinking">
            Reviewing this obligation…
          </p>
        )}

        {errorMessage && (
          <p role="alert" className="text-caption text-status-error m-0">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-space-1">
          <Textarea
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a follow-up — e.g. Who gets paid, and when?"
            aria-label="Follow-up question"
            disabled={pending}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!trimmed || pending}
              onClick={() => ask(trimmed)}
              data-testid="explain-ask-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
